import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, ExternalLink, AlertCircle, Info } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import {
  getHubSpotOAuthUrl,
  disconnectHubSpotIntegration,
  refreshHubSpotTokens
} from '@/lib/apis';
import { hubspotIntegrationAtom } from '@/store';
import { useToast } from '@/hooks/useToast';
import type { HubSpotIntegrationStatus } from '@/lib/types';
import { usePostHog } from 'posthog-js/react';

interface HubSpotIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  integrationStatus: HubSpotIntegrationStatus | null;
}

export function HubSpotIntegrationModal({ 
  isOpen, 
  onClose, 
  integrationStatus 
}: HubSpotIntegrationModalProps) {
  const posthog = usePostHog();
  const [isConnecting, setIsConnecting] = useState(false);
  const [, setHubspotIntegration] = useAtom(hubspotIntegrationAtom);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const connectMutation = useMutation({
    mutationFn: getHubSpotOAuthUrl,
    onSuccess: (data) => {
      posthog?.capture('hubspot_connected');
      // Redirect to HubSpot OAuth
      window.location.href = data.oauth_url;
    },
    onError: (error) => {
      setIsConnecting(false);
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to initiate HubSpot connection',
        variant: 'destructive',
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectHubSpotIntegration,
    onSuccess: (data) => {
      // success=false rides a 200 (e.g. a stale tab disconnecting an already-disconnected
      // integration), so it must be handled here — a throw inside onSuccess escapes React
      // Query's onError and crashes the component. Invalidate anyway so the stale UI resyncs.
      if (!data.success) {
        toast({ title: 'Disconnection Failed', description: data.message, variant: 'destructive' });
        queryClient.invalidateQueries({ queryKey: ['hubspot', 'integration', 'status'] });
        return;
      }
      posthog?.capture('hubspot_disconnected');
      setHubspotIntegration(null);
      queryClient.invalidateQueries({ queryKey: ['hubspot', 'integration', 'status'] });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Disconnection Failed',
        description: error.message || 'Failed to disconnect HubSpot integration',
        variant: 'destructive',
      });
    },
  });

  const refreshTokensMutation = useMutation({
    mutationFn: refreshHubSpotTokens,
    onSuccess: (data) => {
      if (data.success) {
        posthog?.capture('hubspot_tokens_refreshed');
        queryClient.invalidateQueries({ queryKey: ['hubspot', 'integration', 'status'] });
      } else {
        throw new Error(data.message);
      }
    },
    onError: (error) => {
      toast({
        title: 'Token Refresh Failed',
        description: error.message || 'Failed to refresh HubSpot tokens',
        variant: 'destructive',
      });
    },
  });

  const handleConnect = () => {
    setIsConnecting(true);
    connectMutation.mutate();
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate();
  };

  const handleRefreshTokens = () => {
    refreshTokensMutation.mutate();
  };

  const isConnected = integrationStatus?.is_connected || false;
  const isLoading = connectMutation.isPending || disconnectMutation.isPending || refreshTokensMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src="/hubspot.png" alt="HubSpot" className="w-6 h-6" />
            HubSpot Integration
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Connection Status */}
          <div className="flex items-start gap-3 p-4 border rounded-lg">
            {isConnected ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Connected</p>
                  <p className="text-sm text-gray-700">
                    {integrationStatus?.account_name || 'HubSpot Account'}
                  </p>
                  {integrationStatus?.last_sync_at && (
                    <p className="text-xs text-gray-500">
                      Last sync: {new Date(integrationStatus.last_sync_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Not Connected</p>
                  <p className="text-sm text-gray-500">
                    Connect your HubSpot account to sync contact and company data with your ad campaigns for enhanced targeting and lead tracking.
                  </p>
                </div>
              </>
            )}
          </div>

          {isConnected && integrationStatus?.scopes && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Granted Permissions:</h4>
              <div className="flex flex-wrap gap-1">
                {integrationStatus.scopes.map((scope) => (
                  <span
                    key={scope}
                    className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md"
                  >
                    {scope}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Error States */}
          {connectMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {connectMutation.error?.message || 'Failed to connect to HubSpot'}
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {!isConnected ? (
              <Button
                onClick={handleConnect}
                disabled={isLoading || isConnecting}
                className="w-full"
              >
                {isLoading || isConnecting ? (
                  <>
                    <Spinner className="w-4 h-4 mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect HubSpot Account
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                {/* No account switching in any integration modal (#5936): they own the connection
                    lifecycle only. HubSpot additionally has no account concept — the OAuth grant is
                    portal-scoped (one connection = one portal), so changing portal IS reconnecting;
                    the ad platforms switch accounts via the user-menu "Ad Accounts" section. */}
                <Button
                  onClick={handleRefreshTokens}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full"
                >
                  {refreshTokensMutation.isPending ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Refreshing...
                    </>
                  ) : (
                    'Refresh Connection'
                  )}
                </Button>
                
                <Button
                  onClick={handleDisconnect}
                  disabled={isLoading}
                  variant="destructive"
                  className="w-full"
                >
                  {disconnectMutation.isPending ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect HubSpot'
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>• Your HubSpot credentials are encrypted and stored securely</p>
            <p>• You can disconnect at any time from this settings page</p>
            <p>• Only the permissions you grant will be accessible</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}